import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { ToastType } from '@components/Toast/Toast';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  removeVendorFromOrder,
  setVendorToVoid,
} from '@store/orders/actions/vendorActions';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { ICustomerPayment } from '@store/orders/types';

export async function voidVendorContract(vendorId: number) {
  try {
    const response: V4ApiResponse<unknown> = await apiClient.post(
      `VendorContract/Void/${vendorId}`,
      undefined,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.vendorDetailsVoidVendor,
      ]),
    );

    if (!response.isSuccess) {
      throw new Error(response.errors[0]?.message ?? 'Failed to void vendor');
    }
  } catch (error: unknown) {
    logError('voidVendorContract', error as Error);
    throw createError(error, 'Failed to void vendor');
  }
}

export async function voidVendor(
  vendorId: number,
  index: number,
  // insurance vendors keep the original hard delete behavior
  forceDelete = false,
) {
  if (vendorId <= 0) {
    removeVendorFromOrder(index);
    return true;
  }

  if (!forceDelete && isFeatureFlagEnabled(FeatureFlags.FeatureVoidVendors)()) {
    await voidVendorContract(vendorId);
    setVendorToVoid(index);
    return true;
  }

  try {
    const response: { isSuccess: boolean; Message: string } =
      await apiClient.delete(
        `VendorContract/${vendorId}`,
        buildChangeTrackingRequestConfig([
          CHANGE_TRACKING_SOURCES.orderDetails.vendorDetailsVoidVendor,
        ]),
      );
    if (response.isSuccess) {
      removeVendorFromOrder(index);
      handleToast(ToastType.Success, 'Vendor Voided');
      return true;
    }
    throw new Error(response.Message || 'Vendor could not be voided');
  } catch (error: unknown) {
    logError('voidVendor', error as Error);
    throw createError(error, 'Failed to void vendor');
  }
}

export async function requestChecks(efsCheck: {
  amount: number;
  comment: string;
  lineItemId: number;
}) {
  try {
    const response: V4ApiResponse<ICustomerPayment[]> = await apiClient.post(
      'load/RequestCheck',
      efsCheck,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.carrierBillingRequestCheck,
      ]),
    );
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to request check');
    handleToast(ToastType.Error, e.message);
  }
}

export async function vendorEFSCheckRequest(payload: {
  lineItemId: number;
  comment?: string;
  amount?: number;
}) {
  try {
    const response: V4ApiResponse<Record<string, string | number>> =
      await apiClient.post(
        'VendorContract/RequestCheck',
        payload,
        buildChangeTrackingRequestConfig([
          CHANGE_TRACKING_SOURCES.orderDetails.vendorPaymentsRequestCheck,
        ]),
      );
    if (!response.isSuccess) {
      handleToast(
        ToastType.Error,
        response.errors[0]?.message ?? 'Vendor EFS Check Request failed',
      );
      return;
    }
    handleToast(ToastType.Success, 'Vendor EFS Check Requested Successfully');
    return response.value;
  } catch (error: unknown) {
    logError('vendorEFSCheckRequest', error as Error);
    throw new Error('Vendor EFS Check Request failed');
  }
}
